import { useDisclosure } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton, RanaModal, RanaTable } from "@nens/ranalib";
import { TenantContext, TenantUser } from "@nens/ranalib";
import { useTableData } from "@nens/react/hooks";

import { useInstancePermissions } from "@/queries/query-instance-permissions";
import { useTenantListPageQuery } from "@/queries/query-tenant";
import { cancelButtonText } from "@/translations";

export function SwitchOrganisationsModal() {
  const intl = useIntl();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { limit, offset, page, setPage, setPageSize } = useTableData({
    initialPage: 1,
    initialPageSize: 10,
  });

  const [displayAllTenants, setDisplayAllTenants] = useState<boolean>(false);

  const { canListTenant } = useInstancePermissions();

  const tenantContext = useContext(TenantContext);
  const user = tenantContext.user as TenantUser;
  const { data, isLoading } = useTenantListPageQuery({
    limit: limit,
    offset: offset,
    orderBy: "name",
    userId: displayAllTenants ? undefined : user.id,
  });

  const tenants = data?.items ?? [];
  const activeTenantId = tenantContext.tenant;
  const numPages = Math.ceil((data?.total || 1) / limit);

  return (
    <>
      <RanaButton onClick={onOpen} variant="fourthiary">
        <FormattedMessage id="button.switch_organisations" defaultMessage="Switch organisations" />
      </RanaButton>
      <RanaModal
        isOpen={isOpen}
        onClose={onClose}
        topBar={
          <FormattedMessage
            id="switch_organisations"
            defaultMessage="Switch to another organisation"
          />
        }
        maxW="xl"
        buttons={[
          {
            children: cancelButtonText,
            variant: "tertiary",
            onClick: onClose,
          },
        ]}
      >
        <RanaTable
          emptyElement={<RanaTable.Empty />}
          columnDefinitions={[
            {
              width: "table_cell_xxs",
              title: intl.formatMessage({ id: "tenant.switch.code", defaultMessage: "Code" }),
            },
            {
              width: "table_cell_sm",
              title: intl.formatMessage({ id: "tenant.switch.name", defaultMessage: "Name" }),
            },
          ]}
          tableDataIds={tenants.map((tenant) => tenant.id)}
          isSkeleton={isLoading}
        >
          {canListTenant && (
            <RanaTable.Filters
              checkboxes={[
                {
                  label: intl.formatMessage({
                    id: "tenant.switch.all_tenants",
                    defaultMessage: "All tenants",
                  }),
                  checked: displayAllTenants,
                  setChecked: setDisplayAllTenants,
                },
              ]}
            />
          )}
          <RanaTable.Container>
            <RanaTable.Header />
            <RanaTable.Body>
              {tenants.map((tenant) => (
                <RanaTable.Row
                  isDisabled={tenant.id === activeTenantId}
                  key={tenant.id}
                  rowId={tenant.id}
                  to={"/" + tenant.id}
                >
                  <RanaTable.Cell>{tenant.id}</RanaTable.Cell>
                  <RanaTable.Cell>{tenant.name}</RanaTable.Cell>
                </RanaTable.Row>
              ))}
            </RanaTable.Body>
          </RanaTable.Container>
          {numPages > 1 && (
            <RanaTable.Pagination
              limit={limit}
              numPages={numPages}
              page={page}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          )}
        </RanaTable>
      </RanaModal>
    </>
  );
}
